import { Grid } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { RequestDay, RequestType } from '../../models'
import {
  SetShowModalPayload,
  hideHeaderDrawer,
  hideModal,
  hideDrawer,
} from '../../redux/reducers/appSettingsReducer'
import {
  showErrorMessage,
  showInfoMessage,
  showSuccessMessage,
  showWarningMessage,
} from '../../redux/reducers/snackbarReducer'
import {
  setAlerts,
  setComments,
  setConflicts,
  setDateRange,
  setEmployeeSelected,
  setEmployees,
  setFinancialYear,
  setHours,
  setIsIncrease,
  setSelectedDays,
  timeOffRequestsState,
  updateSelectedDay,
} from '../../redux/reducers/timeOffRequestsReducer'
import { RootStore, useAppDispatch } from '../../redux/store'
import { employeeService } from '../../services/employeeService'
import { manualRequestsService } from '../../services/myActionsService'
import { settingsService } from '../../services/settingsService'
import Modal from '../../shared/UI/Modal'
import Tab from '../../shared/UI/Tab'
import { AbsenceDayRequest } from '../../types/absence'
import { ConflictResponse } from '../../types/base-response'
import { OptionValue } from '../../types/option-value'
import { getCalendarRestrictions } from '../../utils/app-utils'
import { formatDateWithTimeZone, isWeekend } from '../../utils/date-utils'
import UserErrorMessage from '../../utils/errorFilter'
import { ErrorList } from '../../utils/errorList'
import { tabType } from '../Absence/types'
import MultiDayRequest from '../MultiDayRequest'

import { buildRequestType1, buildRequestType2, buildRequestType3 } from './manualRequestBuilder'
import {
  ManualRequestType1Request,
  ManualRequestType2Request,
  ManualRequestType3Request,
} from '../../types/manual-request-types'
import { v2MyActionsService } from '../../services/myActionsServiceV2'
import { CallOutEnhancement } from '../CallOutEnhancement/CallOutEnhancement'
import { BuilderArgs, buildEnhancementPostBody } from '../../utils/RequestBuilder/requestBuilder'
import { usePayPeriod } from '../../utils/Hooks/usePayPeriod'
import { isEnhancementByRequestType } from '../../utils/SharedMethods/isEnhancement'
import { calculateTotalRequestedShifts } from '../../utils/SharedMethods/calculateTotalRequestedShifts'
import { useDefaultErrorHandler } from '../../utils/Hooks/useDefaultErrorHandler'
import { calculateCalloutDays } from '../../utils/SharedMethods/calculateCallOutDays'
import {
  validateAbsenceHours,
  validateEnhancementHours,
} from '../../utils/SharedMethods/handleHoursChange'
import { useDefaultHours } from '../../utils/Hooks/useDefaultHours'

function ManualRequest() {
  const [isHidden, setIsHidden] = useState(true)
  const dispatch = useAppDispatch()
  const [defaultShiftLength, setDefaultShiftLength] = useState<number>(8)
  const [disabledDates, setDisabledDates] = useState<string[]>([])
  const [employeeDepartment, setEmployeeDepartment] = useState<OptionValue>()
  const [alertMessage, setAlertMessage] = useState<string | undefined>()
  const [submitLoading, setSubmitLoading] = useState<boolean>(false)
  const {
    dateRange,
    comments,
    adjustmentHours,
    isIncrease,
    financialYear,
    employeeSelected,
    requestType,
    conflicts,
    alerts,
    calloutDetails,
    paidHoursOverride,
    calculatedPaidHours,
  } = useSelector<RootStore, timeOffRequestsState>((state: RootStore) => state.timeOff)
  const { selectedDays } = useSelector((state: RootStore) => state.timeOff, shallowEqual)
  const { userSettings } = useSelector((state: RootStore) => state.appSettings)
  const enhancementsSettingOn = useMemo(() => userSettings?.hasEnhancements, [userSettings])
  const defaultErrorHandler = useDefaultErrorHandler()
  const { showModal, title, message, type, buttonLabel } = useSelector<
    RootStore,
    SetShowModalPayload
  >((state: RootStore) => state.appSettings.modalProps)

  const { employeeDetails } = useSelector((state: RootStore) => state.appSettings)

  const isHTL = useMemo(() => employeeDetails?.isHtl ?? false, [employeeDetails?.isHtl])

  const { directReports } = useSelector((state: RootStore) => state.appSettings)
  const selectedEmployee = directReports.find(emp => emp.employeeId === employeeSelected)
  const selectedEmployeeDepartmentId = selectedEmployee?.departmentId ?? 0
  const payPeriod = usePayPeriod(employeeSelected)

  const { getDefaultEnhancementDayHours, getDefaultEnhancementDayCode, getValidCalculationCodes } = useDefaultHours({
    employeeId: employeeSelected ?? 0,
    departmentId: selectedEmployeeDepartmentId,
    teamId: selectedEmployee?.teamId,
    dateRange,
    type: requestType,
  })

  const getDayHours = useCallback(
    (date: Date) => {
      if (isEnhancementByRequestType(requestType)) {
        const hours = getDefaultEnhancementDayHours(date)
        return hours
      }

      return defaultShiftLength
    },
    [requestType, defaultShiftLength, getDefaultEnhancementDayHours]
  )

  if (employeeSelected && !employeeDepartment) {
    employeeService
      .getEmployeeDepartment(employeeSelected)
      .then(employee => {
        setEmployeeDepartment(employee.department)
      })
      .catch(defaultErrorHandler)
  }

  useEffect(() => {
    if (!selectedEmployee) {
      return
    }

    settingsService
      .getRestrictions(selectedEmployee.departmentId)
      .then(deptRestrictions => {
        const teamRestrictions = {
          holidayRestrictions: deptRestrictions.holidayRestrictions.filter(
            x => x.departmentTeamId === selectedEmployee.teamId
          ),
          deskBookingRestrictions: [],
          errors: deptRestrictions.errors,
          status: deptRestrictions.status,
        }

        setDisabledDates(getCalendarRestrictions(teamRestrictions, requestType))
      })
      .catch(defaultErrorHandler)
  }, [requestType, selectedEmployee])

  useEffect(() => {
    if (dateRange && dateRange[0] && dateRange[1]) {
      const allDays: AbsenceDayRequest[] = []
      const currentDate = formatDateWithTimeZone(dateRange[0])
      if (requestType === RequestType.CALL_OUT_M) {
        allDays.push(...calculateCalloutDays(dateRange))
      } else {
        while (currentDate <= formatDateWithTimeZone(dateRange[1])) {
          const checked =
            isEnhancementByRequestType(requestType) || !isWeekend(new Date(currentDate))
          allDays.push({
            day: formatDateWithTimeZone(currentDate),
            hours: getDayHours(currentDate),
            checked,
            ...(requestType === RequestType.ON_CALL_M
              ? { calculationCode: getDefaultEnhancementDayCode(currentDate) ?? undefined }
              : {}),
          })
          currentDate.setDate(currentDate.getDate() + 1)
        }
      }
      dispatch(setSelectedDays(allDays))
      dispatch(setAlerts([]))
    }
  }, [dateRange, getDayHours, getDefaultEnhancementDayCode])

  const totalRequestedHours = useMemo(() => {
    if (selectedDays?.some(day => day.checked)) {
      const daysToCalculate = selectedDays.filter(x => x.checked)

      if (daysToCalculate.length === 0) {
        return 0
      }

      const selectedHours = daysToCalculate.map(selectedDay =>
        !Number.isNaN(selectedDay.hours!) ? selectedDay.hours! : 0
      )

      return selectedHours.reduce((accumulator, currentValue) => accumulator + currentValue, 0)
    }
    return 0
  }, [selectedDays])

  const totalRequestedShifts = useMemo(
    () => calculateTotalRequestedShifts(selectedDays, getDefaultEnhancementDayCode),
    [selectedDays, getDefaultEnhancementDayCode]
  )

  const updateHours = (selectedDay: Date, hours: string, request: RequestType) => {
    if (selectedDays) {
      const currentDay = selectedDays.find(day => day.day!.getTime() === selectedDay.getTime())
      if (currentDay) {
        const updatedValues = isEnhancementByRequestType(request)
          ? validateEnhancementHours(hours, currentDay.checked)
          : validateAbsenceHours(hours)
        dispatch(updateSelectedDay(selectedDay, updatedValues))
      }
    }
  }

  const updateCheckedDay = (selectedDay: Date, hours: string) => {
    if (selectedDays) {
      const currentDay = selectedDays.find(
        (day: RequestDay) => day.day!.getTime() === selectedDay.getTime()
      )
      if (currentDay) {
        dispatch(updateSelectedDay(selectedDay, { checked: !currentDay.checked }))
      }
    }
  }

  const updateCode = (selectedDay: Date, code: string, request: RequestType) => {
    if (selectedDays) {
      const currentDay = selectedDays.find(day => day.day!.getTime() === selectedDay.getTime())
      if (currentDay) {
        dispatch(updateSelectedDay(selectedDay, { calculationCode: code }))
      }
    }
  }

  const dispatchSubmission = () => {
    dispatch(showSuccessMessage(`Your ${tabType(requestType)} request has been sent`))
    dispatch(hideHeaderDrawer())
    dispatch(setDateRange([null, null]))
    dispatch(setSelectedDays(null))
    dispatch(setIsIncrease(false))
    dispatch(setHours(0))
    dispatch(setFinancialYear(''))
    dispatch(setEmployees(null))
    dispatch(setComments(''))
    dispatch(setEmployeeSelected(null))
  }

  const submitRequest = async (
    body: ManualRequestType1Request | ManualRequestType2Request | ManualRequestType3Request,
    event: React.FormEvent<HTMLFormElement>,
    callType: number
  ) => {
    event.preventDefault()
    setSubmitLoading(true)
    manualRequestsService
      .createManualRequest(body, callType)
      .then(e => {
        setSubmitLoading(false)
        if (e.status === 200 && e.errors.length > 0) {
          e.errors.forEach(error => {
            if (error.name === 'NegativeEntitlement') {
              const alertObj = ErrorList.find(err => err.name === 'NegativeEntitlement')
              dispatch(showInfoMessage(alertObj!.message))
              dispatch(setConflicts(true))
              return
            }
            dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
          })
        } else if (e.status === 201 && conflicts) {
          dispatchSubmission()
        } else {
          dispatchSubmission()
        }
        dispatch(hideHeaderDrawer())
        dispatch(hideDrawer())
      })
      .catch(err => {
        setSubmitLoading(false)
        if (err.response.status === 409) {
          dispatch(setAlerts((err.response.data as ConflictResponse).conflicts))
          dispatch(setConflicts(true))
        } else {
          defaultErrorHandler(err)
        }
      })
  }

  const getBuilderArgs = (): BuilderArgs => ({
    requestType,
    employeeId: employeeSelected!,
    dateRange,
    comments,
    selectedDays,
    financialYear,
    increase: isIncrease,
    hours: adjustmentHours,
    overrideConflicts: conflicts,
    alerts,
    conflicts,
    calloutDetails,
    paidHoursOverride,
    calculatedPaidHours,
  })

  const submitType1 = async (event: React.FormEvent<HTMLFormElement>) => {
    await submitRequest(buildRequestType1(getBuilderArgs()), event, 1)
  }

  const submitType2 = async (event: React.FormEvent<HTMLFormElement>) => {
    await submitRequest(buildRequestType2(getBuilderArgs()), event, 2)
  }

  const submitType3 = async (event: React.FormEvent<HTMLFormElement>) => {
    await submitRequest(buildRequestType3(getBuilderArgs()), event, 3)
  }

  const submitEnhancement = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubmitLoading(true)
    v2MyActionsService
      .postEnhancementManual(
        buildEnhancementPostBody(
          getBuilderArgs(),
          getDefaultEnhancementDayHours,
          getDefaultEnhancementDayCode
        )
      )
      .then(e => {
        setSubmitLoading(false)
        dispatchSubmission()
        dispatch(hideHeaderDrawer())
        dispatch(hideDrawer())
      })
      .catch(err => {
        setSubmitLoading(false)
        if (err.response.status === 409) {
          dispatch(setConflicts(true))
          dispatch(
            showWarningMessage(
              'Calculated paid hours have changed since last calculation. The paid hours have been recalculated.'
            )
          )
        } else {
          defaultErrorHandler(err)
        }
      })
  }

  const handleClose = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    try {
      dispatch(hideHeaderDrawer())
      dispatch(setDateRange([null, null]))
      dispatch(setSelectedDays(null))
      dispatch(setIsIncrease(false))
      dispatch(setHours(0))
      dispatch(setFinancialYear(''))
      dispatch(setEmployees(null))
      dispatch(setComments(''))
    } catch (err) {
      dispatch(showErrorMessage('Something went wrong'))
    }
  }

  useEffect(() => {
    dispatch(setConflicts(false))
    if (employeeSelected) {
      setIsHidden(false)
      employeeService
        .getEmployeeDefaultShiftLength(employeeSelected)
        .then(response => {
          setDefaultShiftLength(response.defaultShiftLength)
        })
        .catch(defaultErrorHandler)
    } else {
      setIsHidden(true)
    }
  }, [employeeSelected, isIncrease, adjustmentHours, financialYear])

  let tabs = [
    {
      label: tabType(RequestType.COURSE),
      tabContext: (
        <Grid item xs={12} mt={5}>
          <MultiDayRequest
            cardTitle={tabType(RequestType.COURSE)}
            type={RequestType.COURSE}
            handleSubmit={submitType2}
            handleClose={(e: any) => handleClose(e)}
            totalRequestedHours={totalRequestedHours}
            updateHours={updateHours}
            updateCheckedDay={updateCheckedDay}
            isManager
            isHidden={isHidden}
            allowIsHidden
            isHTL={isHTL}
            submitLoading={submitLoading}
            dataTestId="ManualRequest"
          />
        </Grid>
      ),
    },
    {
      label: tabType(RequestType.LIEU_DAY),
      tabContext: (
        <Grid item xs={12} mt={5}>
          <MultiDayRequest
            cardTitle={tabType(RequestType.LIEU_DAY_M)}
            type={RequestType.LIEU_DAY_M}
            handleSubmit={submitType2}
            handleClose={(e: any) => handleClose(e)}
            totalRequestedHours={totalRequestedHours}
            updateHours={updateHours}
            updateCheckedDay={updateCheckedDay}
            disabledDates={disabledDates}
            isManager
            isHidden={isHidden}
            allowIsHidden
            isHTL={isHTL}
            submitLoading={submitLoading}
            dataTestId="ManualRequest"
          />
        </Grid>
      ),
    },
    {
      label: tabType(RequestType.HOLIDAY),
      tabContext: (
        <Grid item xs={12} mt={5}>
          <MultiDayRequest
            cardTitle={tabType(RequestType.HOLIDAY_M)}
            type={RequestType.HOLIDAY_M}
            handleSubmit={submitType2}
            handleClose={(e: any) => handleClose(e)}
            totalRequestedHours={totalRequestedHours}
            updateHours={updateHours}
            updateCheckedDay={updateCheckedDay}
            disabledDates={disabledDates}
            isManager
            isHidden={isHidden}
            allowIsHidden
            isHTL={isHTL}
            submitLoading={submitLoading}
            dataTestId="ManualRequest"
          />
        </Grid>
      ),
    },
    {
      label: tabType(RequestType.MATERNITY),
      tabContext: (
        <Grid item xs={12} mt={5}>
          <MultiDayRequest
            cardTitle={tabType(RequestType.MATERNITY)}
            type={RequestType.MATERNITY}
            handleSubmit={submitType3}
            handleClose={(e: any) => handleClose(e)}
            totalRequestedHours={totalRequestedHours}
            updateHours={updateHours}
            updateCheckedDay={updateCheckedDay}
            doesIncludeSummary={false}
            isManager
            isHidden={isHidden}
            allowIsHidden
            isHTL={isHTL}
            submitLoading={submitLoading}
            dataTestId="ManualRequest"
          />
        </Grid>
      ),
    },
    {
      label: tabType(RequestType.PATERNITY),
      tabContext: (
        <Grid item xs={12} mt={5}>
          <MultiDayRequest
            cardTitle={tabType(RequestType.PATERNITY)}
            type={RequestType.PATERNITY}
            handleSubmit={submitType3}
            handleClose={(e: any) => handleClose(e)}
            totalRequestedHours={totalRequestedHours}
            updateHours={updateHours}
            updateCheckedDay={updateCheckedDay}
            doesIncludeSummary={false}
            isManager
            isHidden={isHidden}
            allowIsHidden
            isHTL={isHTL}
            submitLoading={submitLoading}
            dataTestId="ManualRequest"
          />
        </Grid>
      ),
    },
    {
      label: tabType(RequestType.OTHER),
      tabContext: (
        <Grid item xs={12} mt={5}>
          <MultiDayRequest
            cardTitle={tabType(RequestType.OTHER)}
            type={RequestType.OTHER}
            handleSubmit={submitType2}
            handleClose={(e: any) => handleClose(e)}
            totalRequestedHours={totalRequestedHours}
            updateHours={updateHours}
            updateCheckedDay={updateCheckedDay}
            isManager
            isHidden={isHidden}
            allowIsHidden
            isHTL={isHTL}
            submitLoading={submitLoading}
            dataTestId="ManualRequest"
          />
        </Grid>
      ),
    },
    {
      label: tabType(RequestType.NIGHT_SHIFT),
      tabContext: (
        <Grid item xs={12} mt={5}>
          <MultiDayRequest
            cardTitle={tabType(RequestType.SHIFT)}
            type={RequestType.NIGHT_SHIFT}
            handleSubmit={submitType3}
            handleClose={(e: any) => handleClose(e)}
            totalRequestedHours={totalRequestedHours}
            updateHours={updateHours}
            updateCheckedDay={updateCheckedDay}
            doesIncludeSummary={false}
            isManager
            isHidden={isHidden}
            allowIsHidden
            isHTL={isHTL}
            submitLoading={submitLoading}
            dataTestId="ManualRequest"
          />
        </Grid>
      ),
    },
    {
      label: tabType(RequestType.TWILIGHT_SHIFT),
      tabContext: (
        <Grid item xs={12} mt={5}>
          <MultiDayRequest
            cardTitle={tabType(RequestType.TWILIGHT_SHIFT)}
            type={RequestType.TWILIGHT_SHIFT}
            handleSubmit={submitType3}
            handleClose={(e: any) => handleClose(e)}
            totalRequestedHours={totalRequestedHours}
            updateHours={updateHours}
            updateCheckedDay={updateCheckedDay}
            doesIncludeSummary={false}
            isManager
            isHidden={isHidden}
            allowIsHidden
            isHTL={isHTL}
            submitLoading={submitLoading}
            dataTestId="ManualRequest"
          />
        </Grid>
      ),
    },
  ]
  if (enhancementsSettingOn) {
    tabs = [
      ...tabs,
      {
        label: tabType(RequestType.CALL_OUT),
        tabContext: (
          <Grid item xs={12} mt={5}>
            <CallOutEnhancement
              disabledDates={disabledDates}
              isManager
              employeeSelected={employeeSelected}
              isEmployeeSelected={!isHidden}
              handleSubmit={submitEnhancement}
              handleClose={handleClose}
              payPeriod={payPeriod}
            />
          </Grid>
        ),
      },
      {
        label: tabType(RequestType.ON_CALL),
        tabContext: (
          <Grid item xs={12} mt={5}>
            <MultiDayRequest
              cardTitle={tabType(RequestType.ON_CALL_M)}
              type={RequestType.ON_CALL_M}
              handleSubmit={submitEnhancement}
              handleClose={(e: any) => handleClose(e)}
              totalRequestedHours={totalRequestedHours}
              totalRequestedShifts={totalRequestedShifts}
              updateHours={updateHours}
              updateCheckedDay={updateCheckedDay}
              updateCode={updateCode}
              disabledDates={disabledDates}
              isManager
              isHidden={isHidden}
              allowIsHidden
              isHTL={isHTL}
              submitLoading={submitLoading}
              payPeriod={payPeriod}
              getDefaultEnhancementDayCode={getDefaultEnhancementDayCode}
              getValidCalculationCodes={getValidCalculationCodes}
            />
          </Grid>
        ),
      },
      {
        label: tabType(RequestType.OVERTIME),
        tabContext: (
          <Grid item xs={12} mt={5}>
            <MultiDayRequest
              cardTitle={tabType(RequestType.OVERTIME_M)}
              type={RequestType.OVERTIME_M}
              handleSubmit={submitEnhancement}
              handleClose={(e: any) => handleClose(e)}
              totalRequestedHours={totalRequestedHours}
              updateHours={updateHours}
              updateCheckedDay={updateCheckedDay}
              disabledDates={disabledDates}
              isManager
              isHidden={isHidden}
              allowIsHidden
              isHTL={isHTL}
              submitLoading={submitLoading}
              dataTestId="ManualRequest"
              payPeriod={payPeriod}
            />
          </Grid>
        ),
      },

      {
        label: tabType(RequestType.NIGHT),
        tabContext: (
          <Grid item xs={12} mt={5}>
            <MultiDayRequest
              cardTitle={tabType(RequestType.NIGHT_M)}
              type={RequestType.NIGHT_M}
              handleSubmit={submitEnhancement}
              handleClose={(e: any) => handleClose(e)}
              totalRequestedHours={totalRequestedHours}
              updateHours={updateHours}
              updateCheckedDay={updateCheckedDay}
              disabledDates={disabledDates}
              isManager
              isHidden={isHidden}
              allowIsHidden
              isHTL={isHTL}
              submitLoading={submitLoading}
              dataTestId="ManualRequest"
              payPeriod={payPeriod}
            />
          </Grid>
        ),
      },
      {
        label: tabType(RequestType.WEEKEND),
        tabContext: (
          <Grid item xs={12} mt={5}>
            <MultiDayRequest
              cardTitle={tabType(RequestType.WEEKEND)}
              type={RequestType.WEEKEND}
              handleSubmit={submitEnhancement}
              handleClose={async (e: any) => handleClose(e)}
              totalRequestedHours={totalRequestedHours}
              updateHours={updateHours}
              updateCheckedDay={updateCheckedDay}
              disabledDates={disabledDates}
              isManager
              isHidden={isHidden}
              allowIsHidden
              isHTL={isHTL}
              submitLoading={submitLoading}
              dataTestId="ManualRequest"
              payPeriod={payPeriod}
              weekendOnly
            />
          </Grid>
        ),
      },
    ]
  }
  return (
    <>
      <Tab titleAndContent={tabs} dataTestId="ManualRequest" />
      <Modal
        type={type}
        open={showModal}
        onClose={() => {
          dispatch(hideModal())
        }}
        title={title}
        message={alertMessage || message}
        buttonLabel={buttonLabel}
      />
    </>
  )
}

export default ManualRequest
